import { useState, useEffect } from 'react';
import {
    Container,
    Paper,
    Typography,
    Box,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    Chip,
    IconButton,
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    Button,
    CircularProgress,
    Alert,
    TextField,
    Tooltip,
    Stack,
    Divider,
    Grid,
    Tabs,
    Tab,
    LinearProgress,
    Select,
    MenuItem,
    FormControl,
    InputLabel
} from '@mui/material';
import {
    Visibility as VisibilityIcon,
    CheckCircle as ApproveIcon,
    Cancel as RejectIcon,
    Refresh as RefreshIcon,
    Forum as DiscussionIcon,
    Edit as EditIcon,
    Save as SaveIcon,
    Close as CloseIcon
} from '@mui/icons-material';
import { getClientPrincipal } from '../services/auth';
import {
    fetchAllPPDs,
    approvePPD,
    rejectPPD,
    markPPDNeedsDiscussion,
    updatePPDCost,
    updatePPDFinancialYear,
    PPDFormWithCompetencies
} from '../services/ppd';
import {
    sendPPDApproval,
    sendPPDApprovalStakeholderNotifications,
    sendPPDNeedsDiscussion,
    sendPPDRejection
} from '../services/email';
import {
    formatFYLabel,
    getFormFinancialYear,
    getCurrentFinancialYear,
    formatGBP,
    buildFinancialYearOptions
} from '../utils/financialYear';

interface TabPanelProps {
    children?: React.ReactNode;
    index: number;
    value: number;
}

function TabPanel(props: TabPanelProps) {
    const { children, value, index, ...other } = props;
    return (
        <div
            role="tabpanel"
            hidden={value !== index}
            {...other}
        >
            {value === index && <Box sx={{ pt: 3 }}>{children}</Box>}
        </div>
    );
}

// Annual PPD budget (May–April management year)
const ANNUAL_PPD_BUDGET = 15000;

const PPDAdminPage = () => {
    const [ppdForms, setPpdForms] = useState<PPDFormWithCompetencies[]>([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState<string | null>(null);
    const [selectedPPD, setSelectedPPD] = useState<PPDFormWithCompetencies | null>(null);
    const [detailsDialogOpen, setDetailsDialogOpen] = useState(false);
    const [decisionDialogOpen, setDecisionDialogOpen] = useState(false);
    const [decisionType, setDecisionType] = useState<'rejected' | 'needs-discussion'>('rejected');
    const [decisionComments, setDecisionComments] = useState('');
    const [processing, setProcessing] = useState(false);
    const [userEmail, setUserEmail] = useState<string | null>(null);
    const [accessDenied, setAccessDenied] = useState(false);
    const [tabValue, setTabValue] = useState(0);
    const [selectedFY, setSelectedFY] = useState<number>(getCurrentFinancialYear());
    const [editingCostId, setEditingCostId] = useState<number | null>(null);
    const [editingCostValue, setEditingCostValue] = useState<string>('');
    const [editingFinancialYearId, setEditingFinancialYearId] = useState<number | null>(null);
    const [editingFinancialYearValue, setEditingFinancialYearValue] = useState<number>(getCurrentFinancialYear());

    // Authorized admins for PPD
    const PPD_ADMIN_EMAILS = [
        'si@iodparc.com',
        'mark@iodparc.com',
        'hatty@iodparc.com',
        'diane@iodparc.com',
        'victoria@iodparc.com',
        'sadie@iodparc.com'
    ];

    useEffect(() => {
        checkAccess();
    }, []);

    const checkAccess = async () => {
        try {
            const principal = await getClientPrincipal();
            const email = principal?.userDetails?.toLowerCase() || null;
            setUserEmail(email);

            if (!email || !PPD_ADMIN_EMAILS.includes(email)) {
                setAccessDenied(true);
                setLoading(false);
                return;
            }

            loadPPDForms();
        } catch (err) {
            console.error('Error checking access:', err);
            setAccessDenied(true);
            setLoading(false);
        }
    };

    const loadPPDForms = async () => {
        try {
            setLoading(true);
            setError(null);
            const forms = await fetchAllPPDs();
            setPpdForms(forms);
        } catch (err) {
            console.error('Error loading PPD forms:', err);
            setError('Failed to load PPD forms');
        } finally {
            setLoading(false);
        }
    };

    const handleViewDetails = (ppd: PPDFormWithCompetencies) => {
        setSelectedPPD(ppd);
        setDetailsDialogOpen(true);
    };

    const handleApprove = async (ppd: PPDFormWithCompetencies) => {
        try {
            setProcessing(true);
            const principal = await getClientPrincipal();
            if (!principal?.userDetails) {
                throw new Error('User not authenticated');
            }

            await approvePPD(ppd.id!, principal.userDetails);

            // Send approval email
            await sendPPDApproval({
                owner_email: ppd.owner_email,
                owner_name: ppd.owner_name,
                activity: ppd.activity,
                approved_by: principal.userDetails
            });

            await sendPPDApprovalStakeholderNotifications({
                owner_name: ppd.owner_name,
                activity: ppd.activity,
                provider_if_external: ppd.provider_if_external,
                duration: ppd.duration,
                cost_gbp: ppd.cost_gbp
            });

            // Reload forms
            await loadPPDForms();
            setDetailsDialogOpen(false);
        } catch (err) {
            console.error('Error approving PPD:', err);
            alert('Failed to approve PPD form');
        } finally {
            setProcessing(false);
        }
    };

    const handleDecisionClick = (ppd: PPDFormWithCompetencies, type: 'rejected' | 'needs-discussion') => {
        setSelectedPPD(ppd);
        setDecisionType(type);
        setDecisionComments('');
        setDecisionDialogOpen(true);
    };

    const handleDecisionConfirm = async () => {
        if (!selectedPPD) {
            return;
        }

        if (decisionType === 'needs-discussion' && !decisionComments.trim()) {
            alert('Please provide comments for Needs Discussion');
            return;
        }

        try {
            setProcessing(true);
            const principal = await getClientPrincipal();
            if (!principal?.userDetails) {
                throw new Error('User not authenticated');
            }

            if (decisionType === 'rejected') {
                await rejectPPD(selectedPPD.id!, principal.userDetails, decisionComments);

                await sendPPDRejection({
                    owner_email: selectedPPD.owner_email,
                    owner_name: selectedPPD.owner_name,
                    activity: selectedPPD.activity,
                    rejection_reason: decisionComments
                });
            } else {
                await markPPDNeedsDiscussion(selectedPPD.id!, principal.userDetails, decisionComments);

                await sendPPDNeedsDiscussion({
                    owner_email: selectedPPD.owner_email,
                    owner_name: selectedPPD.owner_name,
                    activity: selectedPPD.activity,
                    discussion_comments: decisionComments
                });
            }

            // Reload forms
            await loadPPDForms();
            setDecisionDialogOpen(false);
            setDetailsDialogOpen(false);
        } catch (err) {
            console.error('Error processing PPD decision:', err);
            alert('Failed to process PPD decision');
        } finally {
            setProcessing(false);
        }
    };

    const handleStartEditCost = (ppd: PPDFormWithCompetencies) => {
        setEditingCostId(ppd.id!);
        setEditingCostValue(ppd.cost_gbp != null ? String(ppd.cost_gbp) : '');
    };

    const handleCancelEditCost = () => {
        setEditingCostId(null);
        setEditingCostValue('');
    };

    const handleSaveCost = async (ppd: PPDFormWithCompetencies) => {
        const parsed = parseFloat(editingCostValue);
        if (isNaN(parsed) || parsed < 0) {
            alert('Please enter a valid cost');
            return;
        }

        try {
            setProcessing(true);
            await updatePPDCost(ppd.id!, parsed);
            await loadPPDForms();
            setEditingCostId(null);
            setEditingCostValue('');
        } catch (err) {
            console.error('Error updating PPD cost:', err);
            alert('Failed to update cost');
        } finally {
            setProcessing(false);
        }
    };

    const handleStartEditFinancialYear = (ppd: PPDFormWithCompetencies) => {
        setEditingFinancialYearId(ppd.id!);
        setEditingFinancialYearValue(getFormFinancialYear(ppd) ?? getCurrentFinancialYear());
    };

    const handleCancelEditFinancialYear = () => {
        setEditingFinancialYearId(null);
    };

    const handleSaveFinancialYear = async (ppd: PPDFormWithCompetencies) => {
        try {
            setProcessing(true);
            await updatePPDFinancialYear(ppd.id!, editingFinancialYearValue);
            await loadPPDForms();
            setSelectedFY(editingFinancialYearValue);
            setEditingFinancialYearId(null);
        } catch (err) {
            console.error('Error updating PPD financial year:', err);
            alert('Failed to update financial year');
        } finally {
            setProcessing(false);
        }
    };

    if (loading) {
        return (
            <Container maxWidth={false} sx={{ py: 4, px: { xs: 2, md: 4 }, display: 'flex', justifyContent: 'center' }}>
                <CircularProgress />
            </Container>
        );
    }

    if (accessDenied) {
        return (
            <Container maxWidth={false} sx={{ py: 4, px: { xs: 2, md: 4 } }}>
                <Alert severity="error">
                    You do not have permission to access this page. PPD Admin access is restricted to authorized users.
                </Alert>
            </Container>
        );
    }

    if (error) {
        return (
            <Container maxWidth={false} sx={{ py: 4, px: { xs: 2, md: 4 } }}>
                <Alert severity="error">{error}</Alert>
            </Container>
        );
    }

    // Build the list of financial years present in the data, always including the current FY
    const availableFYs = Array.from(
        new Set<number>([
            getCurrentFinancialYear(),
            ...ppdForms
                .map(getFormFinancialYear)
                .filter((fy): fy is number => fy != null)
        ])
    ).sort((a, b) => b - a);

    const formsInSelectedFY = ppdForms.filter(
        ppd => getFormFinancialYear(ppd) === selectedFY
    );

    const financialYearOptions = buildFinancialYearOptions(
        ppdForms.map(getFormFinancialYear)
    );

    const pendingForms = formsInSelectedFY.filter(ppd => ppd.status === 'pending');
    const pastForms = formsInSelectedFY.filter(ppd => ppd.status !== 'pending');

    // Approved spend for the selected financial year (approved forms only)
    const approvedTotal = formsInSelectedFY
        .filter(ppd => ppd.status === 'approved')
        .reduce((sum, ppd) => sum + (ppd.cost_gbp || 0), 0);

    const budgetPercent = Math.min((approvedTotal / ANNUAL_PPD_BUDGET) * 100, 100);
    const remaining = ANNUAL_PPD_BUDGET - approvedTotal;
    const budgetColor: 'success' | 'warning' | 'error' =
        approvedTotal > ANNUAL_PPD_BUDGET ? 'error' : budgetPercent >= 90 ? 'warning' : 'success';

    const renderCostCell = (ppd: PPDFormWithCompetencies) => {
        if (editingCostId === ppd.id) {
            return (
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                    <TextField
                        value={editingCostValue}
                        onChange={(e) => setEditingCostValue(e.target.value)}
                        size="small"
                        type="number"
                        inputProps={{ min: 0, step: '0.01', style: { padding: '4px 6px' } }}
                        sx={{ width: 90 }}
                        autoFocus
                    />
                    <IconButton
                        onClick={() => handleSaveCost(ppd)}
                        size="small"
                        title="Save cost"
                        color="success"
                        disabled={processing}
                    >
                        <SaveIcon fontSize="small" />
                    </IconButton>
                    <IconButton
                        onClick={handleCancelEditCost}
                        size="small"
                        title="Cancel"
                        disabled={processing}
                    >
                        <CloseIcon fontSize="small" />
                    </IconButton>
                </Box>
            );
        }

        const hasCost = ppd.cost_gbp != null && ppd.cost_gbp > 0;
        return (
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                {hasCost ? (
                    <Typography variant="body2" noWrap>
                        {formatGBP(ppd.cost_gbp!)}
                    </Typography>
                ) : (
                    <Typography variant="caption" color="text.secondary" noWrap>
                        Add cost
                    </Typography>
                )}
                <IconButton
                    onClick={() => handleStartEditCost(ppd)}
                    size="small"
                    title="Edit cost"
                    disabled={processing}
                >
                    <EditIcon fontSize="small" />
                </IconButton>
            </Box>
        );
    };

    const renderFinancialYearCell = (ppd: PPDFormWithCompetencies) => {
        if (editingFinancialYearId === ppd.id) {
            return (
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                    <Select
                        value={editingFinancialYearValue}
                        onChange={(event) => setEditingFinancialYearValue(Number(event.target.value))}
                        size="small"
                        aria-label={`Financial year for ${ppd.owner_name}`}
                        sx={{ minWidth: 154 }}
                    >
                        {financialYearOptions.map((fy) => (
                            <MenuItem key={fy} value={fy}>
                                {formatFYLabel(fy)}
                            </MenuItem>
                        ))}
                    </Select>
                    <IconButton
                        onClick={() => handleSaveFinancialYear(ppd)}
                        size="small"
                        title="Save financial year"
                        color="success"
                        disabled={processing}
                    >
                        <SaveIcon fontSize="small" />
                    </IconButton>
                    <IconButton
                        onClick={handleCancelEditFinancialYear}
                        size="small"
                        title="Cancel"
                        disabled={processing}
                    >
                        <CloseIcon fontSize="small" />
                    </IconButton>
                </Box>
            );
        }

        const financialYear = getFormFinancialYear(ppd);
        return (
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                <Typography variant="body2" noWrap>
                    {financialYear == null ? 'Not assigned' : formatFYLabel(financialYear)}
                </Typography>
                <IconButton
                    onClick={() => handleStartEditFinancialYear(ppd)}
                    size="small"
                    title="Reassign financial year"
                    disabled={processing}
                >
                    <EditIcon fontSize="small" />
                </IconButton>
            </Box>
        );
    };

    const getStatusColor = (status: string): "default" | "warning" | "success" | "error" | "info" => {
        switch (status) {
            case 'pending':
                return 'warning';
            case 'approved':
                return 'success';
            case 'rejected':
                return 'error';
            case 'needs-discussion':
                return 'info';
            default:
                return 'default';
        }
    };

    return (
        <Container maxWidth={false} sx={{ py: 4, px: { xs: 2, md: 4 } }}>
            <Paper elevation={3} sx={{ p: 3 }}>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3, gap: 2, flexWrap: 'wrap' }}>
                    <Typography variant="h4" sx={{ color: '#009688' }}>
                        PPD Admin
                    </Typography>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                        <FormControl size="small" sx={{ minWidth: 200 }}>
                            <InputLabel id="ppd-fy-label">Financial Year</InputLabel>
                            <Select
                                labelId="ppd-fy-label"
                                label="Financial Year"
                                value={selectedFY}
                                onChange={(e) => setSelectedFY(Number(e.target.value))}
                            >
                                {availableFYs.map((fy) => (
                                    <MenuItem key={fy} value={fy}>
                                        {formatFYLabel(fy)}
                                    </MenuItem>
                                ))}
                            </Select>
                        </FormControl>
                        <Button
                            variant="outlined"
                            startIcon={<RefreshIcon />}
                            onClick={loadPPDForms}
                        >
                            Refresh
                        </Button>
                    </Box>
                </Box>

                {/* Budget summary for the selected financial year */}
                <Box sx={{ mb: 3, p: 2, bgcolor: 'background.default', borderRadius: 1, border: 1, borderColor: 'divider' }}>
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline', mb: 1, flexWrap: 'wrap', gap: 1 }}>
                        <Typography variant="subtitle1" fontWeight="bold">
                            Budget {formatFYLabel(selectedFY)}
                        </Typography>
                        <Typography variant="body2" color="text.secondary">
                            {formatGBP(approvedTotal)} approved of {formatGBP(ANNUAL_PPD_BUDGET)}
                            {remaining >= 0
                                ? ` · ${formatGBP(remaining)} remaining`
                                : ` · ${formatGBP(Math.abs(remaining))} over budget`}
                        </Typography>
                    </Box>
                    <LinearProgress
                        variant="determinate"
                        value={budgetPercent}
                        color={budgetColor}
                        sx={{ height: 10, borderRadius: 5 }}
                    />
                </Box>

                <Box sx={{ borderBottom: 1, borderColor: 'divider' }}>
                    <Tabs value={tabValue} onChange={(_, newValue) => setTabValue(newValue)}>
                        <Tab label={`Pending (${pendingForms.length})`} />
                        <Tab label={`Past Forms (${pastForms.length})`} />
                    </Tabs>
                </Box>

                {/* Pending Forms Tab */}
                <TabPanel value={tabValue} index={0}>
                    {pendingForms.length === 0 ? (
                        <Alert severity="info">
                            No pending PPD forms to review.
                        </Alert>
                    ) : (
                        <TableContainer>
                            <Table size="small" sx={{ tableLayout: 'fixed' }}>
                                <TableHead>
                                    <TableRow>
                                        <TableCell sx={{ width: '16%' }}>Employee</TableCell>
                                        <TableCell sx={{ width: '20%' }}>Activity</TableCell>
                                        <TableCell sx={{ width: '14%' }}>Job Title</TableCell>
                                        <TableCell sx={{ width: '16%' }}>Competencies</TableCell>
                                        <TableCell sx={{ width: '12%' }}>Cost</TableCell>
                                        <TableCell sx={{ width: '10%' }}>Submitted</TableCell>
                                        <TableCell align="right" sx={{ width: '12%' }}>Actions</TableCell>
                                    </TableRow>
                                </TableHead>
                                <TableBody>
                                    {pendingForms.map((ppd) => (
                                        <TableRow key={ppd.id}>
                                            <TableCell>
                                                <Typography variant="body2" fontWeight="bold" noWrap>
                                                    {ppd.owner_name}
                                                </Typography>
                                                <Typography variant="caption" color="text.secondary" noWrap>
                                                    {ppd.owner_email}
                                                </Typography>
                                            </TableCell>
                                            <TableCell>
                                                <Tooltip title={ppd.activity || '-'}>
                                                    <Typography variant="body2" noWrap>
                                                        {ppd.activity || '-'}
                                                    </Typography>
                                                </Tooltip>
                                            </TableCell>
                                            <TableCell>
                                                <Tooltip title={ppd.job_title || '-'}>
                                                    <Typography variant="body2" noWrap>
                                                        {ppd.job_title || '-'}
                                                    </Typography>
                                                </Tooltip>
                                            </TableCell>
                                            <TableCell>
                                                <Tooltip title={ppd.competencies || 'None'}>
                                                    <Typography variant="caption" noWrap sx={{ display: 'block' }}>
                                                        {ppd.competencies || 'None'}
                                                    </Typography>
                                                </Tooltip>
                                            </TableCell>
                                            <TableCell>
                                                {renderCostCell(ppd)}
                                            </TableCell>
                                            <TableCell sx={{ whiteSpace: 'nowrap' }}>
                                                {ppd.submission_date 
                                                    ? new Date(ppd.submission_date).toLocaleDateString()
                                                    : ppd.created_at
                                                    ? new Date(ppd.created_at).toLocaleDateString()
                                                    : 'N/A'}
                                            </TableCell>
                                            <TableCell align="right" sx={{ whiteSpace: 'nowrap' }}>
                                                <IconButton
                                                    onClick={() => handleViewDetails(ppd)}
                                                    size="small"
                                                    title="View details"
                                                >
                                                    <VisibilityIcon />
                                                </IconButton>
                                                <IconButton
                                                    onClick={() => handleApprove(ppd)}
                                                    size="small"
                                                    title="Approve"
                                                    color="success"
                                                    disabled={processing}
                                                >
                                                    <ApproveIcon />
                                                </IconButton>
                                                <IconButton
                                                    onClick={() => handleDecisionClick(ppd, 'needs-discussion')}
                                                    size="small"
                                                    title="Needs discussion"
                                                    color="info"
                                                    disabled={processing}
                                                >
                                                    <DiscussionIcon />
                                                </IconButton>
                                                <IconButton
                                                    onClick={() => handleDecisionClick(ppd, 'rejected')}
                                                    size="small"
                                                    title="Reject"
                                                    color="error"
                                                    disabled={processing}
                                                >
                                                    <RejectIcon />
                                                </IconButton>
                                            </TableCell>
                                        </TableRow>
                                    ))}
                                </TableBody>
                            </Table>
                        </TableContainer>
                    )}
                </TabPanel>

                {/* Past Forms Tab */}
                <TabPanel value={tabValue} index={1}>
                    {pastForms.length === 0 ? (
                        <Alert severity="info">
                            No past PPD forms.
                        </Alert>
                    ) : (
                        <TableContainer>
                            <Table size="small" sx={{ tableLayout: 'fixed' }}>
                                <TableHead>
                                    <TableRow>
                                        <TableCell sx={{ width: '14%' }}>Employee</TableCell>
                                        <TableCell sx={{ width: '15%' }}>Activity</TableCell>
                                        <TableCell sx={{ width: '11%' }}>Job Title</TableCell>
                                        <TableCell sx={{ width: '9%' }}>Cost</TableCell>
                                        <TableCell sx={{ width: '17%' }}>Financial Year</TableCell>
                                        <TableCell sx={{ width: '9%' }}>Submitted</TableCell>
                                        <TableCell sx={{ width: '9%' }}>Status</TableCell>
                                        <TableCell sx={{ width: '10%' }}>Decision By</TableCell>
                                        <TableCell align="right" sx={{ width: '6%' }}>Actions</TableCell>
                                    </TableRow>
                                </TableHead>
                                <TableBody>
                                    {pastForms.map((ppd) => (
                                        <TableRow key={ppd.id}>
                                            <TableCell>
                                                <Typography variant="body2" fontWeight="bold" noWrap>
                                                    {ppd.owner_name}
                                                </Typography>
                                                <Typography variant="caption" color="text.secondary" noWrap>
                                                    {ppd.owner_email}
                                                </Typography>
                                            </TableCell>
                                            <TableCell>
                                                <Tooltip title={ppd.activity || '-'}>
                                                    <Typography variant="body2" noWrap>
                                                        {ppd.activity || '-'}
                                                    </Typography>
                                                </Tooltip>
                                            </TableCell>
                                            <TableCell>
                                                <Tooltip title={ppd.job_title || '-'}>
                                                    <Typography variant="body2" noWrap>
                                                        {ppd.job_title || '-'}
                                                    </Typography>
                                                </Tooltip>
                                            </TableCell>
                                            <TableCell>
                                                {renderCostCell(ppd)}
                                            </TableCell>
                                            <TableCell>
                                                {renderFinancialYearCell(ppd)}
                                            </TableCell>
                                            <TableCell sx={{ whiteSpace: 'nowrap' }}>
                                                {ppd.submission_date 
                                                    ? new Date(ppd.submission_date).toLocaleDateString()
                                                    : ppd.created_at
                                                    ? new Date(ppd.created_at).toLocaleDateString()
                                                    : 'N/A'}
                                            </TableCell>
                                            <TableCell>
                                                <Chip
                                                    label={ppd.status.toUpperCase()}
                                                    color={getStatusColor(ppd.status)}
                                                    size="small"
                                                />
                                            </TableCell>
                                            <TableCell>
                                                <Tooltip title={ppd.approved_by || 'N/A'}>
                                                    <Typography variant="body2" noWrap>
                                                        {ppd.approved_by || 'N/A'}
                                                    </Typography>
                                                </Tooltip>
                                            </TableCell>
                                            <TableCell align="right" sx={{ whiteSpace: 'nowrap' }}>
                                                <IconButton
                                                    onClick={() => handleViewDetails(ppd)}
                                                    size="small"
                                                    title="View details"
                                                >
                                                    <VisibilityIcon />
                                                </IconButton>
                                            </TableCell>
                                        </TableRow>
                                    ))}
                                </TableBody>
                            </Table>
                        </TableContainer>
                    )}
                </TabPanel>
            </Paper>

            {/* Details Dialog */}
            <Dialog 
                open={detailsDialogOpen} 
                onClose={() => setDetailsDialogOpen(false)}
                maxWidth="md"
                fullWidth
            >
                <DialogTitle sx={{ bgcolor: '#009688', color: 'white' }}>
                    PPD Form Details
                </DialogTitle>
                <DialogContent sx={{ mt: 2 }}>
                    {selectedPPD && (
                        <Stack spacing={2}>
                            {/* Employee Info */}
                            <Grid container spacing={2}>
                                <Grid item xs={12} md={6}>
                                    <Typography variant="subtitle2" color="text.secondary">
                                        Employee
                                    </Typography>
                                    <Typography variant="body1" fontWeight="bold">
                                        {selectedPPD.owner_name}
                                    </Typography>
                                    <Typography variant="body2" color="text.secondary">
                                        {selectedPPD.owner_email}
                                    </Typography>
                                </Grid>
                                <Grid item xs={12} md={6}>
                                    <Typography variant="subtitle2" color="text.secondary">
                                        Manager Email
                                    </Typography>
                                    <Typography variant="body1">
                                        {selectedPPD.manager_email}
                                    </Typography>
                                </Grid>
                            </Grid>

                            <Divider />

                            {/* Header Information */}
                            <Grid container spacing={2}>
                                <Grid item xs={12} md={6}>
                                    <Typography variant="subtitle2" color="text.secondary">
                                        Job Title
                                    </Typography>
                                    <Typography variant="body1">
                                        {selectedPPD.job_title || '-'}
                                    </Typography>
                                </Grid>
                                <Grid item xs={12} md={6}>
                                    <Typography variant="subtitle2" color="text.secondary">
                                        Activity
                                    </Typography>
                                    <Typography variant="body1">
                                        {selectedPPD.activity || '-'}
                                    </Typography>
                                </Grid>
                                <Grid item xs={12} md={6}>
                                    <Typography variant="subtitle2" color="text.secondary">
                                        Provider
                                    </Typography>
                                    <Typography variant="body1">
                                        {selectedPPD.provider_if_external || '-'}
                                    </Typography>
                                </Grid>
                                <Grid item xs={12} md={6}>
                                    <Typography variant="subtitle2" color="text.secondary">
                                        Duration
                                    </Typography>
                                    <Typography variant="body1">
                                        {selectedPPD.duration || '-'}
                                    </Typography>
                                </Grid>
                                <Grid item xs={12} md={6}>
                                    <Typography variant="subtitle2" color="text.secondary">
                                        Cost (GBP)
                                    </Typography>
                                    <Typography variant="body1">
                                        £{selectedPPD.cost_gbp || 0}
                                    </Typography>
                                </Grid>
                                <Grid item xs={12} md={6}>
                                    <Typography variant="subtitle2" color="text.secondary">
                                        Financial Year
                                    </Typography>
                                    <Typography variant="body1">
                                        {getFormFinancialYear(selectedPPD) == null
                                            ? 'Not assigned'
                                            : formatFYLabel(getFormFinancialYear(selectedPPD)!)}
                                    </Typography>
                                </Grid>
                            </Grid>

                            <Divider />

                            {/* Questions */}
                            <Box>
                                <Typography variant="subtitle1" fontWeight="bold" gutterBottom>
                                    1. Development Need
                                </Typography>
                                <Typography variant="body2" sx={{ whiteSpace: 'pre-wrap' }}>
                                    {selectedPPD.development_need || '-'}
                                </Typography>
                            </Box>

                            <Box>
                                <Typography variant="subtitle1" fontWeight="bold" gutterBottom>
                                    2. Competencies Addressed
                                </Typography>
                                <Typography variant="body2">
                                    {selectedPPD.competencies || 'None selected'}
                                </Typography>
                            </Box>

                            <Box>
                                <Typography variant="subtitle1" fontWeight="bold" gutterBottom>
                                    3. Options Considered
                                </Typography>
                                <Typography variant="body2" sx={{ whiteSpace: 'pre-wrap' }}>
                                    {selectedPPD.options_considered || '-'}
                                </Typography>
                            </Box>

                            <Box>
                                <Typography variant="subtitle1" fontWeight="bold" gutterBottom>
                                    4. Resources Required
                                </Typography>
                                <Typography variant="body2" sx={{ whiteSpace: 'pre-wrap' }}>
                                    {selectedPPD.resources_required || '-'}
                                </Typography>
                            </Box>

                            <Box>
                                <Typography variant="subtitle1" fontWeight="bold" gutterBottom>
                                    5. Timescales
                                </Typography>
                                <Typography variant="body2" sx={{ whiteSpace: 'pre-wrap' }}>
                                    {selectedPPD.timescales || '-'}
                                </Typography>
                            </Box>

                            <Box>
                                <Typography variant="subtitle1" fontWeight="bold" gutterBottom>
                                    6. Effectiveness Assessment
                                </Typography>
                                <Typography variant="body2" sx={{ whiteSpace: 'pre-wrap' }}>
                                    {selectedPPD.effectiveness_assessment || '-'}
                                </Typography>
                            </Box>

                            <Box>
                                <Typography variant="subtitle1" fontWeight="bold" gutterBottom>
                                    7. Expected Changes
                                </Typography>
                                <Typography variant="body2" sx={{ whiteSpace: 'pre-wrap' }}>
                                    {selectedPPD.expected_changes || '-'}
                                </Typography>
                            </Box>

                            <Box>
                                <Typography variant="subtitle1" fontWeight="bold" gutterBottom>
                                    8. Knowledge Sharing
                                </Typography>
                                <Typography variant="body2" sx={{ whiteSpace: 'pre-wrap' }}>
                                    {selectedPPD.knowledge_sharing || '-'}
                                </Typography>
                            </Box>

                            <Box>
                                <Typography variant="subtitle1" fontWeight="bold" gutterBottom>
                                    9. Proposed Option
                                </Typography>
                                <Typography variant="body2" sx={{ whiteSpace: 'pre-wrap' }}>
                                    {selectedPPD.proposed_option || '-'}
                                </Typography>
                            </Box>

                            {selectedPPD.status !== 'pending' && selectedPPD.admin_comments && (
                                <Box>
                                    <Typography variant="subtitle1" fontWeight="bold" gutterBottom>
                                        Admin Comments / Expectations
                                    </Typography>
                                    <Typography variant="body2" sx={{ whiteSpace: 'pre-wrap' }}>
                                        {selectedPPD.admin_comments}
                                    </Typography>
                                </Box>
                            )}
                        </Stack>
                    )}
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setDetailsDialogOpen(false)}>
                        Close
                    </Button>
                    <Button
                        onClick={() => handleDecisionClick(selectedPPD!, 'needs-discussion')}
                        startIcon={<DiscussionIcon />}
                        color="info"
                        disabled={processing}
                    >
                        Needs Discussion
                    </Button>
                    <Button
                        onClick={() => handleDecisionClick(selectedPPD!, 'rejected')}
                        startIcon={<RejectIcon />}
                        color="error"
                        disabled={processing}
                    >
                        Reject
                    </Button>
                    <Button
                        onClick={() => handleApprove(selectedPPD!)}
                        startIcon={<ApproveIcon />}
                        color="success"
                        variant="contained"
                        disabled={processing}
                    >
                        {processing ? 'Processing...' : 'Approve'}
                    </Button>
                </DialogActions>
            </Dialog>

            {/* Decision Comments Dialog */}
            <Dialog open={decisionDialogOpen} onClose={() => setDecisionDialogOpen(false)}>
                <DialogTitle>
                    {decisionType === 'needs-discussion' ? 'Mark as Needs Discussion' : 'Reject PPD Form'}
                </DialogTitle>
                <DialogContent>
                    <Typography gutterBottom>
                        {decisionType === 'needs-discussion'
                            ? 'Please provide comments explaining why this needs discussion and your expectations if the staff member undertakes the L&D activity.'
                            : 'You can optionally provide comments explaining the rationale for rejection and your expectations if the staff member undertakes the L&D activity.'}
                    </Typography>
                    <TextField
                        fullWidth
                        multiline
                        rows={4}
                        label={decisionType === 'needs-discussion' ? 'Comments (Required)' : 'Comments (Optional)'}
                        value={decisionComments}
                        onChange={(e) => setDecisionComments(e.target.value)}
                        sx={{ mt: 2 }}
                        placeholder="e.g., Please provide more detail on expected outcomes, success measures, and conditions for approval..."
                    />
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setDecisionDialogOpen(false)} disabled={processing}>
                        Cancel
                    </Button>
                    <Button
                        onClick={handleDecisionConfirm}
                        variant="contained"
                        color={decisionType === 'needs-discussion' ? 'info' : 'error'}
                        disabled={processing || (decisionType === 'needs-discussion' && !decisionComments.trim())}
                    >
                        {processing
                            ? 'Processing...'
                            : decisionType === 'needs-discussion'
                            ? 'Confirm Needs Discussion'
                            : 'Confirm Reject'}
                    </Button>
                </DialogActions>
            </Dialog>
        </Container>
    );
};

export default PPDAdminPage;
